export function parseVideoResolution(
    resolution: string | null | undefined,
): { width: number; height: number } {
    const match = String(resolution ?? "").match(/(\d+)x(\d+)/);
    if (!match) {
        return { width: 0, height: 0 };
    }

    return {
        width: parseInt(match[1], 10),
        height: parseInt(match[2], 10),
    };
}

export async function resolveVideoDimensionsFromInput(
    input: { getPrimaryVideoTrack: () => Promise<any> } | null | undefined,
): Promise<{ width: number; height: number }> {
    if (!input || typeof input.getPrimaryVideoTrack !== "function") {
        return { width: 0, height: 0 };
    }

    try {
        const videoTrack = await input.getPrimaryVideoTrack();
        if (!videoTrack) {
            return { width: 0, height: 0 };
        }

        const displayWidth = Number(videoTrack.displayWidth ?? 0);
        const displayHeight = Number(videoTrack.displayHeight ?? 0);
        if (displayWidth > 0 && displayHeight > 0) {
            return { width: displayWidth, height: displayHeight };
        }

        const codedWidth = Number(videoTrack.codedWidth ?? 0);
        const codedHeight = Number(videoTrack.codedHeight ?? 0);
        if (codedWidth > 0 && codedHeight > 0) {
            return { width: codedWidth, height: codedHeight };
        }
    } catch {}

    return { width: 0, height: 0 };
}

/**
 * Match the existing sidebar "Insert into script" behavior for initial video sizing.
 * Same-as-canvas aspect ratios are fit to canvas; mismatched ratios keep source dimensions.
 */
export function calculateVideoVisualDimensions(
    videoWidth: number,
    videoHeight: number,
    canvasWidth: number,
    canvasHeight: number,
): { width: number; height: number; x: number; y: number } {
    if (!videoWidth || !videoHeight || videoWidth <= 0 || videoHeight <= 0) {
        return {
            width: canvasWidth,
            height: canvasHeight,
            x: canvasWidth / 2,
            y: canvasHeight / 2,
        };
    }

    const videoAspect = videoWidth / videoHeight;
    const canvasAspect = canvasWidth / canvasHeight;
    const aspectTolerance = 0.05;
    const isSameAspect =
        Math.abs(videoAspect - canvasAspect) / canvasAspect < aspectTolerance;

    let finalWidth: number;
    let finalHeight: number;

    if (isSameAspect) {
        if (videoAspect > canvasAspect) {
            finalWidth = canvasWidth;
            finalHeight = canvasWidth / videoAspect;
        } else {
            finalHeight = canvasHeight;
            finalWidth = canvasHeight * videoAspect;
        }
    } else {
        finalWidth = videoWidth;
        finalHeight = videoHeight;
    }

    return {
        width: finalWidth,
        height: finalHeight,
        x: canvasWidth / 2,
        y: canvasHeight / 2,
    };
}
